import React, {Component} from 'react'

import styled from '@emotion/styled'
import sv from '../../styles/styles'
import {Code, Link} from 'react-feather'
import VideoThumbnail from '../VideoThumbnail'
import copy from 'copy-to-clipboard';
import {withToastManager} from 'react-toast-notifications';
import {withTranslation} from "react-i18next";

import {connect} from "react-redux";

import {gistActions, sourceActions} from "../../_actions";
import {sourceConstants} from "../../_constants";
import moment from 'moment'
import EdiText from 'react-editext'
import {portfolioActionTypes} from "../../_constants/actions/analytics/portfolioActionTypes";
import {AnimatedLoader} from "../../styles/CommonStyler";

// STYLE ##########################################################

const Container = styled(`div`)`
  width: 100%;
  height: 100%;
  min-height: 330px;
  overflow-y: auto;
  padding-bottom: 20px;
`;

const DetailsHeader = styled(`div`)`
  padding: 48px;
  flex: 0;
  ${sv.flexRow};
  flex-wrap: wrap;
  @media(max-width: 480px) {
    ${sv.flexColumn};
    padding: 24px;
  }
  align-items: flex-start;
  gap: 20px;
`;

const Thumbnail = styled(`div`)`
  align-self: center;
`;

const Info = styled(`div`)`
  flex: 1;
`;

const Name = styled(`div`)`
  margin: 16px 0;
  ${sv.h1};
  @media(max-width: 520px) {
    /* px is fallback if vw isn't supported */
    font-size: 11px; 
    font-size: 4vw;
  }
`;

const Sub = styled(`div`)`
  ${sv.label};
  text-transform: none;
  color: ${sv.colors.grey};
  padding-top: ${props => props.paddingTop};
`;

const Actions = styled(`div`)`
  ${sv.flexColumn};
  padding: 0 48px;
  @media(max-width: 480px) {
    padding: 0 12px;
  }
`;

const Action = styled('div')`
  margin: 10px 0;
`;

const ActionTitle = styled('div')`
  ${sv.h4};
  margin: 5px;
`;

const LinkStyle = styled(`div`)`
  ${sv.buttonBase};
  background: ${sv.colors.subtleGrey};
  color: ${sv.colors.grey};
  flex: 1;
  justify-content: left;
  width: 100%;
  cursor: auto;
`;

const CodeIcon = styled(Code)`
  min-width: 24px;
  max-height: calc(100% - 6px);
`;

const LinkIcon = styled(Link)`
  min-width: 24px;
  max-height: calc(100% - 6px);
`;

const LinkText = styled(`div`)`
  overflow-wrap: anywhere;
  margin-left: 16px;
`;

const CodeStyle = styled('div')`
  color: ${sv.colors.darkGray};
  font-family: "Courier New", Courier, monospace;
  text-align: left;
  cursor: text;
`;

const CopyButton = styled('a')`
  float: right;
  color: ${sv.colors.cta};
  font-weight: 500;
  font-size: 15px;
  margin: 5px;
  text-align: right;
  cursor: pointer;
`;

const LinkDisclosure = styled('div')`
  float: left;
  color: ${sv.colors.darkGray};
  font-size: 14px;
  font-style: italic;
  margin-top: 5px;
  margin-left: 20px;
`;

const OutlineButton = styled(`div`)`
  ${sv.buttonBase};
  border: 2px solid ${props => props.red ? 'rgba(238,83,34,.5)' : 'rgba(244,173,70,.5)'};
  color: ${props => props.red ? 'rgba(238,83,34,1)' : 'rgba(244,173,70,1)'};
  &:hover {
    border-color: ${props => props.red ? 'rgba(238,83,34,1)' : 'rgba(244,173,70,1)'};
  }
  @media (max-width: 480px) {
    margin-top: 10px;
    padding: 5px;
  }
`;

const StyledEdiText = styled(EdiText)`
  button {
  }
  button[editext="edit-button"] {
    display: none;    // hides edit button on view display
  }
  button[editext="save-button"], button[editext="cancel-button"] {
    background: ${sv.colors.cta}; 
    color: white;
    border-radius: 5px;
    margin: 3px 4px;
    box-shadow: 0 2px 3px rgba(0,0,0,.2);
    &:hover {
      box-shadow: 0 3px 6px rgba(0,0,0,.5);
    }
  }
  input, textarea {
    ${sv.h1}
    padding: 0.3em;
    border: 3px solid ${sv.colors.cta}; 
    border-radius: 5px;
    width: 96%;
    @media(max-width: 520px) {
      /* px is fallback if vw isn't supported */
      font-size: 11px; 
      font-size: 4vw;
    }
  }  
  div[editext="view-container"] div:first-child {
    padding: 0.3em;
    border: 3px solid rgba(255,255,255,0); 
    &:hover {
      background: ${sv.colors.subtleGrey};
      border-radius: 5px;
    }
  }  
  div[editext="edit-container"] {
    display: block;
    position: relative;
  }  
  div[editext="edit-container"] div:last-child {
    position: absolute;
    right: 0;
  }
`;

// COMPONENT ##########################################################

class MediaDetails extends Component {
  
  copyToClipboard(link) {
    const {t, toastManager} = this.props;
    copy(link);
    toastManager.add(
        t('Copied to clipboard'),
        {
          appearance: 'success',
          autoDismiss:  true
        }
    );
    this.props.copyUrl();
  }

  handleEdit = (value, item) => {
    if(value === item.name || value === "") { return; } // if unchanged or empty, do not process
    this.props.editSource(item.id, sourceConstants.SOURCE_TITLE, value);  // send the request
  };

  formatTrim = (time) => {
    return time._data.hours > 0 ? 'H:mm:ss' : 'm:ss'
  };

  autoSelect = (ev) => {
    if (window.getSelection) {
      let range = document.createRange();
      range.selectNode(ev.target);
      window.getSelection().removeAllRanges();
      window.getSelection().addRange(range);
    }
  }

  render() {

    const { t, currentSource }  = this.props;
    const { selectedItem } = currentSource;
    const item = selectedItem;
    const totalTime = moment.duration(item.duration, 'seconds');
    const formattedTime = moment.utc(totalTime.asMilliseconds()).format(this.formatTrim(totalTime));

    const statusText = () => {
      if (item.status === sourceConstants.SOURCE_STATE.ACTIVE) {
        return t("Active");
      } else if (item.status === sourceConstants.SOURCE_STATE.PENDING) {
        return t("Gist Pending");
      } else {
        return t("Inactive");
      }
    };

    const subText = () => {
      return (item.status === sourceConstants.SOURCE_STATE.PENDING)
              ? t("Ordered", {uploadTimeAgo: item.uploaded})
              : t("Created", {uploadTimeAgo: item.uploaded});
    };

    return (
      <Container>
        <DetailsHeader>
          <Thumbnail>
            <VideoThumbnail maxWidth={250} maxHeight={140} image={item.stickerUrl} altText={item.title} />
          </Thumbnail>
          <Info>
            <Sub paddingTop={"10px"}>{statusText()}</Sub>
            <Name>
              <StyledEdiText
                  value={item.name}                       // value
                  onSave={(e) => this.handleEdit(e,item)} // save handler
                  editOnViewClick                         // edit on elem click
                  submitOnEnter                           // save on 'Enter' press
                  cancelOnEscape                          // cancel on 'Esc' press
                  cancelOnUnfocus                         // cancel on 'un-focus'
                  showButtonsOnHover                      // hide edit button when not focused
                  onEditingStart={()=>this.props.editTitleStart()} // announce that title is being edited
                  onCancel={()=>this.props.editTitleCancel()}      // announce that title edit is canceled
              />
            </Name>
            <Sub>{subText()}</Sub>
            {item.status === sourceConstants.SOURCE_STATE.ACTIVE &&
              <Sub>{t("Viewed", {viewCount: item.viewed})}</Sub>
            }
            <Sub>{t("Duration",{durationTime: formattedTime})}</Sub>

            {/* load spinner if the source's properties are edited, and awaiting response. */}
            {currentSource.isEditing &&
            <AnimatedLoader color={sv.colors.cta}/>
            }

          </Info>
        </DetailsHeader>

        <Actions>
          <Action>
            <ActionTitle>{t('Player URL')}</ActionTitle>
            <LinkStyle>
              <LinkIcon size={24} />
              <LinkText><a href={"/play/" + item.id}>{item.url}</a></LinkText>
            </LinkStyle>
            <LinkDisclosure>{t('Anyone with this link can view this content.')}</LinkDisclosure>
            <CopyButton onClick={() => this.copyToClipboard(item.url)}>{t("Copy")}</CopyButton>
          </Action>
          <Action>
            <ActionTitle>{t('Embed')}</ActionTitle>
            <LinkStyle>
              <CodeIcon size={24} />
              <LinkText><CodeStyle onClick={this.autoSelect}>{item.embedCode}</CodeStyle></LinkText>
            </LinkStyle>
            <CopyButton onClick={() => this.copyToClipboard(item.embedCode)}>{t("Copy")}</CopyButton>
          </Action>

          <Action>
          {item.status === sourceConstants.SOURCE_STATE.ACTIVE &&
          <OutlineButton red
                         onClick={() => this.props.deactivateSource(item.id)}
          >
            Deactivate
          </OutlineButton>
          }

          {item.status === sourceConstants.SOURCE_STATE.DEACTIVATED &&
          <OutlineButton
                         onClick={() => this.props.activateSource(item.id)}
          >
            Activate
          </OutlineButton>
          }
          </Action>

        </Actions>
      </Container>
    )
  };

}

function mapStateToProps(state) {
  const { authentication, sources, currentSource } = state;
  const { user } = authentication;

  return {
    user, sources, currentSource
  };
}

function mapDispatchToProps(dispatch) {
  return {
    getGist           : (uuid)                => dispatch( gistActions.getGist(uuid) ),
    deactivateSource  : (uuid)                => dispatch( sourceActions.deactivateSource(uuid) ),
    activateSource    : (uuid)                => dispatch( sourceActions.activateSource(uuid) ),
    editSource        : (uuid, param, value)  => dispatch( sourceActions.editSource(uuid, param, value)),
    copyUrl           : ()                    => dispatch({type: portfolioActionTypes.COPY_URL}),
    editTitleStart    : ()                    => dispatch({type: portfolioActionTypes.SOURCE_RENAME_TITLE_START}),
    editTitleCancel   : ()                    => dispatch({type: portfolioActionTypes.SOURCE_RENAME_TITLE_CANCEL}),
  }
}

const connectedMediaDetails = withTranslation()(connect(mapStateToProps, mapDispatchToProps)(withToastManager(MediaDetails)));
export { connectedMediaDetails as MediaDetails };
